"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { useRouter } from "next/navigation";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  Menu,
  User,
  FileText,
  TrendingUp,
  Globe,
  X,
  ArrowUp,
  ArrowDown,
  FileCheck,
  Zap,
  Home,
  LogOut,
  LayoutDashboard,
  CircuitBoard,
  TvMinimalPlay,
  ShoppingBag,
} from "lucide-react";
import Image from "next/image";
import { cn } from "@/lib/utils";
import { usePathname } from "next/navigation";
import { useAuth } from "@/hooks/use-auth";
import { createClient } from "@supabase/supabase-js";
import LanguageSelector from "./language-selector";
import { ThemeToggle } from "@/components/theme-toggle";
import { useTheme } from "next-themes";
import { Language } from "@/types/auction-types";

const supabase = createClient(
  process.env.NEXT_PUBLIC_SUPABASE_URL!,
  process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
);
function Navigation({
  highlightSolutions = false,
}: {
  highlightSolutions?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  const pathname = usePathname();
  const isHomePage = pathname === "/";
  const isBuyerDashboard = pathname === "/dashboard/buyer";
  const isSellerDashboard = pathname === "/dashboard/seller";
  const isRoleDashboard = isBuyerDashboard || isSellerDashboard;
  const { user, logout, isAuthenticated, selectedMode } = useAuth();
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const [profile, setProfile] = useState<{
    avatar_url: string;
    fname: string;
    lname: string;
  } | null>(null);
  const [formData, setFormData] = useState<{
    productName: string;
    language: Language;
  }>({
    productName: "",
    language: "en",
  });
  const router = useRouter();

  const handleLanguageChange = (lang: Language) => {
    setFormData((prev) => ({ ...prev, language: lang }));
  };

  useEffect(() => {
    // Sync language state with googtrans cookie
    const getCookie = (name: string) => {
      const match = document.cookie.match(
        new RegExp("(^|;\\s*)" + name + "=([^;]*)"),
      );
      return match ? decodeURIComponent(match[2]) : undefined;
    };

    const cookieValue = getCookie("googtrans");
    if (cookieValue) {
      const parts = cookieValue.split("/");
      const lang = parts[parts.length - 1] as Language;
      if (["en", "fr", "es", "hi", "de", "zh", "ja", "ar"].includes(lang)) {
        setFormData((prev) => ({ ...prev, language: lang }));
      }
    }

    setMounted(true);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);

      // Only track sections on homepage
      if (!isHomePage) return;

      const sections = [
        { id: "hero", element: document.querySelector("#hero") },
        {
          id: "platform-overview",
          element: document.querySelector("#platform-overview"),
        },
        {
          id: "auction-types",
          element: document.querySelector("#auction-types"),
        },
        {
          id: "deployment-options",
          element: document.querySelector("#deployment-options"),
        },
      ];

      const scrollPosition = window.scrollY + 100; // Offset for header

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section.element) {
          const rect = section.element.getBoundingClientRect();
          const elementTop = rect.top + window.scrollY;

          if (scrollPosition >= elementTop) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Initial call
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isHomePage]);

  // Detailed navigation items (simplified)
  const detailedNavItems = [
    {
      title: "Solutions",
      items: [
        {
          title: "Forward Auction",
          href: "/solutions/forward-auction",
          description: "Traditional ascending-price auctions",
          icon: <TrendingUp className="h-5 w-5 text-brand-500" />,
          subtypes: [
            {
              title: "English Auction",
              href: "/solutions/forward-auction/english",
              description: "Classic ascending bid format",
              icon: <ArrowUp className="h-4 w-4 text-brand-500" />,
            },
            {
              title: "Silent Auction",
              href: "/solutions/forward-auction/silent",
              description: "Private multiple bidding",
              icon: <Zap className="h-4 w-4 text-orange-600" />,
            },
            {
              title: "Sealed Bid Auction",
              href: "/solutions/forward-auction/sealed-bid",
              description: "Private one-time bidding",
              icon: <FileCheck className="h-4 w-4 text-purple-500" />,
            },
          ],
        },
        {
          title: "Reverse Auction",
          href: "/solutions/reverse-auction",
          description: "Procurement-focused bidding",
          icon: <TrendingUp className="h-5 w-5 text-success rotate-180" />,
          subtypes: [
            {
              title: "Standard Reverse Auction",
              href: "/solutions/reverse-auction/english-reverse",
              description: "Sellers bid down the price",
              icon: <ArrowDown className="h-4 w-4 text-success" />,
            },
            {
              title: "Ranked Reverse Auction - RFQ (Request for Quote)",
              href: "/solutions/reverse-auction/rfq",
              description: "Rank based competitive supplier selection",
              icon: <FileText className="h-4 w-4 text-orange-500" />,
            },
            {
              title: "Sealed Reverse Auction - RFP (Request for Proposal)",
              href: "/solutions/reverse-auction/rfp",
              description: "Comprehensive proposal requests",
              icon: <FileCheck className="h-4 w-4 text-blue-500" />,
            },
          ],
        },
        {
          title: "Auction Marketplace",
          href: "/auctions",
          description: "Multi-vendor marketplace solution",
          icon: <Globe className="h-5 w-5 text-purple-500" />,
        },
      ],
    },
  ];

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user?.id) return;
      const { data, error } = await supabase
        .from("profiles")
        .select("avatar_url, fname, lname")
        .eq("id", user.id)
        .single();

      if (error) {
        console.error("Failed to fetch profile", error.message);
      } else {
        setProfile(data);
      }
    };

    fetchProfile();
  }, [user]);

  const [hostname, setHostname] = useState("");

  useEffect(() => {
    // Detect hostname on client side
    if (typeof window !== "undefined") {
      const host = window.location.hostname;
      setHostname(host);
      console.log("Detected hostname:", host); // Debug log
    }
  }, []);

  // Determine logo based on hostname or authentication state
  const getLogoSrc = () => {
    // During SSR / before mount, always return the default logo to prevent hydration mismatch
    if (!mounted) {
      return "/briskon-auction-logo.svg";
    }

    const isDark = resolvedTheme === "dark";
    const search = typeof window !== "undefined" ? window.location.search : "";

    // Check if hostname is forward auction domain (with variations)
    if (
      hostname === "forwardauction.briskon.com" ||
      hostname === "www.forwardauction.briskon.com" ||
      hostname.includes("forwardauction")
    ) {
      console.log("Using forward auction logo for hostname:", hostname); // Debug log
      return isDark
        ? "/forwardauction-dark.svg"
        : "/briskon-forward-auction-logo.svg";
    }

    // Check if hostname is reverse auction domain (with variations)
    if (
      hostname === "reverseauction.briskon.com" ||
      hostname === "www.reverseauction.briskon.com" ||
      hostname.includes("reverseauction")
    ) {
      console.log("Using reverse auction logo for hostname:", hostname); // Debug log
      return isDark
        ? "/reverseauction-dark.svg"
        : "/briskon-reverse-auction-logo.svg";
    }

    // Check URL search params (for localhost ?mode=forward / ?mode=reverse)
    if (search.includes("mode=forward")) {
      return isDark
        ? "/forwardauction-dark.svg"
        : "/briskon-forward-auction-logo.svg";
    }

    if (search.includes("mode=reverse")) {
      return isDark
        ? "/reverseauction-dark.svg"
        : "/briskon-reverse-auction-logo.svg";
    }

    // Prefer an explicit URL mode, then use the authenticated dashboard mode.
    const hasExplicitMode = search.includes("mode=");
    const effectiveMode = search.includes("mode=forward")
      ? "forward"
      : search.includes("mode=reverse")
        ? "reverse"
        : hasExplicitMode
          ? null
          : selectedMode;

    if (effectiveMode === "forward") {
      return isDark
        ? "/forwardauction-dark.svg"
        : "/briskon-forward-auction-logo.svg";
    }

    if (effectiveMode === "reverse") {
      return isDark
        ? "/reverseauction-dark.svg"
        : "/briskon-reverse-auction-logo.svg";
    }

    // Default: no mode / marketplace
    return isDark ? "/briskon-logo-dark.svg" : "/briskon-auction-logo.svg";
  };

  const logoSrc = getLogoSrc();
  const currentSearch = mounted ? window.location.search : "";
  const isReverseMode =
    mounted &&
    (hostname === "reverseauction.briskon.com" ||
      hostname === "www.reverseauction.briskon.com" ||
      hostname.includes("reverseauction") ||
      currentSearch.includes("mode=reverse") ||
      selectedMode === "reverse");
  return (
    <header
      className={cn(
        "top-0 z-50 transition-all duration-300 ease-in-out",
        isRoleDashboard
          ? "relative h-14 w-full border-b border-neutral-200 bg-white dark:border-[#2D3748] dark:bg-[#0D1117]"
          : "fixed w-full border-b border-neutral-200 bg-white/95 backdrop-blur-sm dark:border-[#2D3748] dark:bg-[#0D1117]",
        scrolled && !isRoleDashboard && "shadow-soft",
      )}
    >
      <div
        className={cn(
          isRoleDashboard
            ? "h-full w-full px-8 sm:px-10 lg:px-12"
            : "container mx-auto px-4",
        )}
      >
        <div
          className={cn(
            "flex items-center",
            isRoleDashboard
              ? "h-full justify-end gap-4"
              : "h-14 justify-between",
          )}
        >
          {/* Logo */}
          {!isRoleDashboard && (
            <Link
              href="/"
              className="flex items-center space-x-3 group transition-opacity duration-300"
            >
              <div className="relative flex items-center gap-3 overflow-hidden rounded-lg transition-transform duration-300 group-hover:scale-105">
                <Image
                  src={logoSrc}
                  alt="Auction Platform"
                  width={120}
                  height={32}
                  className="h-8 w-auto object-contain"
                  priority
                />
              </div>
            </Link>
          )}

          {/* Desktop Navigation */}
          <NavigationMenu
            className={cn("hidden lg:flex", isRoleDashboard && "ml-auto")}
          >
            <NavigationMenuList className="space-x-2">
              {/* Home Link */}
              {!isRoleDashboard && (
                <NavigationMenuItem>
                  <NavigationMenuLink asChild>
                    <Link
                      href="/"
                      className={cn(
                        "group inline-flex h-9 w-max items-center justify-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors duration-200",
                        "text-neutral-700 hover:text-brand-500 focus:text-brand-500",
                        "dark:text-[#A0AEC0] dark:hover:text-[#FFFFFF] dark:focus:text-[#FFFFFF]",
                      )}
                    >
                      <Home className="h-4 w-4" />
                      <span>Home</span>
                    </Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>
              )}

              {/* Dashboard Link */}
              {isAuthenticated && !isRoleDashboard && (
                <NavigationMenuItem>
                  <NavigationMenuLink asChild>
                    <Link
                      href={
                        user?.role === "both"
                          ? "/dashboard"
                          : user?.role === "seller"
                            ? "/dashboard/seller"
                            : user?.role === "buyer"
                              ? "/dashboard/buyer"
                              : "/"
                      }
                      className={cn(
                        "group inline-flex h-9 w-max items-center justify-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors duration-200",
                        "text-neutral-700 hover:text-brand-500 focus:text-brand-500",
                        "dark:text-[#A0AEC0] dark:hover:text-[#FFFFFF] dark:focus:text-[#FFFFFF]",
                      )}
                    >
                      <LayoutDashboard className="h-4 w-4" />
                      <span>Dashboard</span>
                    </Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>
              )}
              {/* Live demo */}
              <NavigationMenuItem>
                <NavigationMenuLink asChild>
                  <Link
                    href="/auctions"
                    className="group inline-flex h-7 w-max items-center justify-center rounded-md border border-blue-200 bg-blue-50 px-2.5 text-xs font-medium text-black transition-colors duration-200 hover:border-blue-300 hover:bg-blue-100 hover:text-black dark:border-blue-900 dark:bg-blue-950/40 dark:text-white dark:hover:bg-blue-950/70"
                  >
                    <TvMinimalPlay className="mr-1.5 h-3.5 w-3.5" />
                    {isReverseMode ? "Live Reverse Auctions" : "Live Auctions"}
                  </Link>
                </NavigationMenuLink>
              </NavigationMenuItem>
              {/* buy now */}
              {!isReverseMode && (
                <NavigationMenuItem>
                  <NavigationMenuLink asChild>
                    <Link
                      href="/buyNow"
                      className="group inline-flex h-7 w-max items-center justify-center rounded-md border border-blue-200 bg-blue-50 px-2.5 text-xs font-medium text-black transition-colors duration-200 hover:border-blue-300 hover:bg-blue-100 hover:text-black dark:border-blue-900 dark:bg-blue-950/40 dark:text-white dark:hover:bg-blue-950/70"
                    >
                      <ShoppingBag className="mr-1.5 h-3.5 w-3.5" />
                      Buy Now
                    </Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>
              )}
            </NavigationMenuList>
          </NavigationMenu>
          {/* Right side actions */}
          <div className="flex items-center space-x-3">
            {isAuthenticated ? (
              <>
                <div className="hidden md:flex items-center space-x-2">
                  {!isRoleDashboard && (
                    <div className="mr-1 flex items-center gap-2">
                      <ThemeToggle />
                      <LanguageSelector
                        value={formData.language}
                        onChange={handleLanguageChange}
                      />
                    </div>
                  )}

                  {isRoleDashboard ? (
                    <div className="flex items-center gap-3 border-l border-slate-200 pl-5 dark:border-slate-700">
                      <p className="max-w-64 truncate text-[13px] text-slate-600 dark:text-slate-300">
                        Welcome,{" "}
                        <span className="font-semibold text-slate-950 dark:text-white">
                          {[
                            profile?.fname || user?.fname,
                            profile?.lname || user?.lname,
                          ]
                            .filter(Boolean)
                            .join(" ") || "User"}
                        </span>
                      </p>
                      <div className="relative h-7 w-7 shrink-0 overflow-hidden rounded-full ring-1 ring-slate-300 dark:ring-slate-600">
                        <Image
                          src={
                            profile?.avatar_url
                              ? `${profile.avatar_url}?t=${Date.now()}`
                              : "/images/user.png"
                          }
                          alt={profile?.fname || "User"}
                          width={28}
                          height={28}
                          className="h-full w-full object-cover"
                        />
                      </div>
                    </div>
                  ) : (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <div className="w-8 h-8 rounded-full overflow-hidden border border-gray-300 hover:ring-2 hover:ring-gray-400 transition ml-2 cursor-pointer">
                          <Image
                            src={
                              profile?.avatar_url
                                ? `${profile.avatar_url}?t=${Date.now()}`
                                : "/images/user.png"
                            }
                            alt={profile?.fname || "User"}
                            width={32}
                            height={32}
                            className="w-full h-full object-cover rounded-full"
                          />
                        </div>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56">
                        <DropdownMenuLabel>
                          <div className="flex flex-col space-y-1">
                            <p className="text-sm font-medium leading-none">
                              {profile?.fname}
                            </p>
                            <p className="text-xs leading-none text-muted-foreground">
                              {user?.email}
                            </p>
                          </div>
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem asChild>
                          <Link
                            href="/settings/profile"
                            className="cursor-pointer"
                          >
                            <User className="mr-2 h-4 w-4" />
                            <span>View Profile</span>
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={logout}
                          className="cursor-pointer text-red-600 focus:text-red-600"
                        >
                          <LogOut className="mr-2 h-4 w-4" />
                          <span>Log Out</span>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="mr-1 flex items-center gap-2">
                  <ThemeToggle />
                  <LanguageSelector
                    value={formData.language}
                    onChange={handleLanguageChange}
                  />
                </div>
                <Button
                  variant="ghost"
                  className="hidden md:inline-flex text-neutral-700 hover:text-brand-500 hover:bg-neutral-100 transition-all duration-200 dark:text-[#A0AEC0] dark:hover:text-[#FFFFFF] dark:hover:bg-[#1C252E]"
                  asChild
                >
                  <Link href="/login">Login</Link>
                </Button>
                {/* dark:bg-[#1C252E] dark:hover:bg-[#2D3748] dark:border
                      dark:border-[#2D3748] */}
                <Button
                  variant="outline"
                  className="hidden md:inline-flex border-neutral-300 text-neutral-700
                   hover:bg-neutral-50 hover:border-brand-300 hover:text-brand-600 
                   transition-all duration-200 dark:border-[#2D3748] dark:text-[#FFFFFF]  dark:hover:bg-[#2D3748]
                    dark:hover:text-[#FFFFFF] dark:bg-[#1C252E]"
                  asChild
                >
                  <Link href="/register">Sign Up</Link>
                </Button>
              </>
            )}
            {/* Mobile menu */}
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-neutral-700  hover:bg-neutral-100 hover:text-brand-500 transition-colors duration-200 dark:text-[#A0AEC0] dark:hover:text-[#FFFFFF] dark:hover:bg-[#1C252E]"
                >
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent
                side="right"
                className="w-[320px] sm:w-[400px] p-0 bg-white dark:bg-[#0D1117] dark:border-l dark:border-[#2D3748]"
              >
                <div className="flex flex-col h-full">
                  {/* Mobile Header */}
                  <div className="flex items-center justify-between p-4 border-b border-neutral-200 bg-white dark:border-[#2D3748] dark:bg-[#0D1117]">
                    <Link
                      href="/"
                      className="flex items-center space-x-3"
                      onClick={() => setIsOpen(false)}
                    >
                      <Image
                        src={logoSrc}
                        alt="Auction Platform"
                        width={120}
                        height={35}
                        className="h-8 w-auto object-contain dark:brightness-0 dark:invert"
                      />
                    </Link>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setIsOpen(false)}
                      className="text-neutral-700 hover:text-brand-500 hover:bg-neutral-100 dark:text-[#A0AEC0] dark:hover:bg-[#1C252E] dark:hover:text-[#FFFFFF]"
                    >
                      <X className="h-5 w-5" />
                    </Button>
                  </div>

                  {/* Mobile Navigation */}
                  <nav className="flex-1 overflow-y-auto p-6">
                    <div className="space-y-6">
                      {isAuthenticated && (
                        <div className="p-3 bg-neutral-100 dark:bg-[#1C252E] rounded-lg">
                          <div className="text-sm font-medium text-neutral-900 dark:text-[#FFFFFF]">
                            Welcome back, {user?.fname}
                          </div>
                          <div className="text-xs text-neutral-600 dark:text-[#A0AEC0]">
                            {user?.role} account
                          </div>
                        </div>
                      )}
                      <Link
                        href="/"
                        className={cn(
                          "flex items-center gap-3 p-3 rounded-lg hover:bg-neutral-100 hover:text-brand-600 transition-all duration-200 dark:text-[#A0AEC0] dark:hover:bg-[#1C252E] dark:hover:text-[#FFFFFF]",
                          isHomePage &&
                            activeSection === "hero" &&
                            "bg-neutral-100 text-brand-600 dark:bg-[#1C252E] dark:text-[#3B82F6]",
                        )}
                        onClick={() => setIsOpen(false)}
                      >
                        <Home className="h-5 w-5" />
                        <span className="text-sm font-medium">Home</span>
                      </Link>

                      {/* Mobile Detailed Navigation */}
                      {detailedNavItems.map((section) => (
                        <div key={section.title} className="space-y-3">
                          <div className="font-semibold text-sm text-neutral-900 uppercase tracking-wide border-b border-neutral-200 pb-2 dark:text-[#A0AEC0] dark:border-[#2D3748]">
                            {section.title}
                          </div>
                          <div className="space-y-2 pl-2">
                            {section.items.map((item) => (
                              <div key={item.href}>
                                <Link
                                  href={item.href}
                                  className="group flex items-center gap-3 rounded-lg p-3 transition-smooth hover:bg-neutral-50 hover:shadow-soft dark:hover:bg-[#1C252E] card-hover"
                                  onClick={() => setIsOpen(false)}
                                >
                                  {item.icon && (
                                    <div className="flex-shrink-0 hover-lift">
                                      {item.icon}
                                    </div>
                                  )}
                                  <div>
                                    <div className="text-sm font-medium text-neutral-900 dark:text-[#FFFFFF]">
                                      {item.title}
                                    </div>
                                    <div className="text-xs text-neutral-600 mt-1 dark:text-[#A0AEC0]">
                                      {item.description}
                                    </div>
                                  </div>
                                </Link>
                                {/* Mobile Subtypes */}
                                {item.subtypes && (
                                  <div className="ml-6 mt-2 space-y-1">
                                    {item.subtypes.map((subtype) => (
                                      <Link
                                        key={subtype.href}
                                        href={subtype.href}
                                        className="flex items-center gap-2 p-2 rounded-md hover:bg-neutral-100 transition-all duration-200 dark:hover:bg-neutral-800"
                                        onClick={() => setIsOpen(false)}
                                      >
                                        {subtype.icon && (
                                          <div className="flex-shrink-0">
                                            {subtype.icon}
                                          </div>
                                        )}
                                        <div>
                                          <div className="text-xs font-medium text-neutral-700 dark:text-neutral-300">
                                            {subtype.title}
                                          </div>
                                        </div>
                                      </Link>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}

                      <div className="space-y-3">
                        <div className="font-semibold text-sm text-neutral-900 uppercase tracking-wide border-b border-neutral-200 pb-2 dark:text-[#A0AEC0] dark:border-[#2D3748]">
                          Quick Access
                        </div>
                        <div className="space-y-2 pl-2">
                          <Link
                            href="/resources"
                            className="flex items-center gap-3 p-3 rounded-lg hover:bg-neutral-100 hover:text-brand-600 transition-all duration-200 dark:text-[#A0AEC0] dark:hover:bg-[#1C252E] dark:hover:text-[#3B82F6]"
                            onClick={() => setIsOpen(false)}
                          >
                            <FileText className="h-4 w-4" />
                            <span className="text-sm font-medium">
                              Resources
                            </span>
                          </Link>
                          <Link
                            href="/login"
                            className="flex items-center gap-3 p-3 rounded-lg hover:bg-neutral-100 hover:text-brand-600 transition-all duration-200 dark:text-[#A0AEC0] dark:hover:bg-[#1C252E] dark:hover:text-[#3B82F6]"
                            onClick={() => setIsOpen(false)}
                          >
                            <span className="text-sm font-medium">Login</span>
                          </Link>
                          <Link
                            href="/contact"
                            className="flex items-center gap-3 p-3 rounded-lg hover:bg-neutral-100 hover:text-brand-600 transition-all duration-200 dark:text-[#A0AEC0] dark:hover:bg-[#1C252E] dark:hover:text-[#3B82F6]"
                            onClick={() => setIsOpen(false)}
                          >
                            <span className="text-sm font-medium">
                              Contact Sales
                            </span>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </nav>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}

export default Navigation;
